import { useEffect, useState } from "react";
import axios from "axios";

import { AdminLayout } from "../components/AdminLayout";
import { getAuthHeader } from "../context/AuthContext";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  Loader2,
  Truck,
  CheckCircle2,
  Settings2,
} from "lucide-react";

import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function UberConfigurationPage() {

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [settings, setSettings] = useState({

    // -----------------------------------
    // RESTAURANT
    // -----------------------------------

    restaurant_name: "",

    email: "",

    first_name: "",

    last_name: "",

    phone_number: "",

    street1: "",

    street2: "",

    city: "",

    state: "",

    zipcode: "",

    country_iso2: "AU",

    // -----------------------------------
    // UBER
    // -----------------------------------

    uber_client_id: "",

    uber_client_secret: "",

    uber_customer_id: "",

    uber_webhook_secret: "",

    uber_configured: false,
  });

  // ------------------------------------------------------
  // FETCH SETTINGS
  // ------------------------------------------------------

  useEffect(() => {
    fetchUberSettings();
  }, []);

  const fetchUberSettings = async () => {

    try {

      setLoading(true);

      const response = await axios.get(
        `${API}/delivery/uber/settings`,
        {
          headers: getAuthHeader(),
        }
      );

      if (response.data?.data) {

        setSettings((prev) => ({
          ...prev,
          ...response.data.data,
        }));
      }

    } catch (error) {

      console.error(error);

      toast.error(
        "Failed to load Uber settings"
      );

    } finally {

      setLoading(false);
    }
  };

  // ------------------------------------------------------
  // HANDLE CHANGE
  // ------------------------------------------------------

  const handleChange = (key, value) => {

    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // ------------------------------------------------------
  // VALIDATE
  // ------------------------------------------------------

  const validateForm = () => {

    const requiredFields = [

      "restaurant_name",

      "email",

      "phone_number",

      "street1",

      "city",

      "state",

      "zipcode",

      "country_iso2",

      "uber_client_id",

      "uber_client_secret",

      "uber_customer_id",

      "uber_webhook_secret",
    ];

    for (const field of requiredFields) {

      if (!settings[field]?.toString().trim()) {

        toast.error(
          `${field.replaceAll("_", " ")} is required`
        );

        return false;
      }
    }

    return true;
  };

  // ------------------------------------------------------
  // SAVE SETTINGS
  // ------------------------------------------------------

  const saveUberSettings = async () => {

    try {

      if (!validateForm()) {
        return;
      }

      setSaving(true);

      await axios.post(
        `${API}/delivery/uber/settings`,
        settings,
        {
          headers: getAuthHeader(),
        }
      );

      toast.success(
        "Uber settings saved successfully"
      );

      setSettings((prev) => ({
        ...prev,
        uber_configured: true,
      }));

    } catch (error) {

      console.error(error);

      toast.error(
        error?.response?.data?.detail ||
        "Failed to save Uber settings"
      );

    } finally {

      setSaving(false);
    }
  };

  return (

    <AdminLayout>

      <div className="p-8 max-w-6xl mx-auto">

        {/* HEADER */}

        <div className="mb-8">

          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Uber Direct Configuration
          </h1>

          <p className="text-gray-500">
            Configure Uber Direct credentials and
            restaurant pickup details
          </p>

        </div>

        {loading ? (

          <div className="flex items-center gap-3 text-gray-600">

            <Loader2
              className="animate-spin"
              size={20}
            />

            Loading...

          </div>

        ) : (

          <>

            {/* STATUS CARD */}

            <Card className="mb-8 shadow-md border-0 rounded-2xl">

              <CardHeader>

                <CardTitle className="flex items-center gap-3">

                  <Truck size={22} />

                  Uber Direct Status

                </CardTitle>

              </CardHeader>

              <CardContent>

                {settings?.uber_configured ? (

                  <div className="border rounded-2xl p-5 bg-green-50">

                    <div className="flex items-center gap-2 text-green-700 mb-3">

                      <CheckCircle2 size={20} />

                      <span className="font-semibold">
                        Uber Direct Configured
                      </span>

                    </div>

                    <div className="grid md:grid-cols-2 gap-4">

                      <div>

                        <p className="text-sm text-gray-600 mb-1">
                          Uber Customer ID
                        </p>

                        <div className="bg-white border rounded-lg p-3 text-sm break-all font-mono">
                          {settings?.uber_customer_id}
                        </div>

                      </div>

                      <div>

                        <p className="text-sm text-gray-600 mb-1">
                          Restaurant
                        </p>

                        <div className="bg-white border rounded-lg p-3 text-sm">
                          {settings?.restaurant_name}
                        </div>

                      </div>

                    </div>

                  </div>

                ) : (

                  <div className="border rounded-2xl p-5">

                    <div className="flex items-center gap-2 mb-3">

                      <Settings2 size={20} />

                      <span className="font-semibold">
                        Uber Direct Not Configured
                      </span>

                    </div>

                    <p className="text-sm text-gray-500">
                      Enter Uber Direct credentials and
                      restaurant information to enable deliveries.
                    </p>

                  </div>

                )}

              </CardContent>

            </Card>

            {/* FORM */}

            <Card className="shadow-md border-0 rounded-2xl">

              <CardHeader>

                <CardTitle>
                  Uber Direct Settings
                </CardTitle>

              </CardHeader>

              <CardContent>

                {/* RESTAURANT SECTION */}

                <div className="mb-8">

                  <h2 className="text-lg font-semibold mb-4">
                    Restaurant Information
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                    <div>

                      <Label>
                        Restaurant Name *
                      </Label>

                      <Input
                        placeholder="Restaurant Name"
                        value={settings.restaurant_name}
                        onChange={(e) =>
                          handleChange(
                            "restaurant_name",
                            e.target.value
                          )
                        }
                      />

                    </div>

                    <div>

                      <Label>
                        Email *
                      </Label>

                      <Input
                        type="email"
                        placeholder="admin@restaurant.com"
                        value={settings.email}
                        onChange={(e) =>
                          handleChange(
                            "email",
                            e.target.value
                          )
                        }
                      />

                    </div>

                    <div>

                      <Label>
                        First Name
                      </Label>

                      <Input
                        placeholder="First Name"
                        value={settings.first_name}
                        onChange={(e) =>
                          handleChange(
                            "first_name",
                            e.target.value
                          )
                        }
                      />

                    </div>

                    <div>

                      <Label>
                        Last Name
                      </Label>

                      <Input
                        placeholder="Last Name"
                        value={settings.last_name}
                        onChange={(e) =>
                          handleChange(
                            "last_name",
                            e.target.value
                          )
                        }
                      />

                    </div>

                    <div>

                      <Label>
                        Phone Number *
                      </Label>

                      <Input
                        placeholder="+61411111111"
                        value={settings.phone_number}
                        onChange={(e) =>
                          handleChange(
                            "phone_number",
                            e.target.value
                          )
                        }
                      />

                    </div>

                    <div>

                      <Label>
                        Street Address *
                      </Label>

                      <Input
                        placeholder="Street Address"
                        value={settings.street1}
                        onChange={(e) =>
                          handleChange(
                            "street1",
                            e.target.value
                          )
                        }
                      />

                    </div>

                    <div>

                      <Label>
                        Address Line 2
                      </Label>

                      <Input
                        placeholder="Apartment / Suite"
                        value={settings.street2}
                        onChange={(e) =>
                          handleChange(
                            "street2",
                            e.target.value
                          )
                        }
                      />

                    </div>

                    <div>

                      <Label>
                        City *
                      </Label>

                      <Input
                        placeholder="City"
                        value={settings.city}
                        onChange={(e) =>
                          handleChange(
                            "city",
                            e.target.value
                          )
                        }
                      />

                    </div>

                    <div>

                      <Label>
                        State *
                      </Label>

                      <Input
                        placeholder="State"
                        value={settings.state}
                        onChange={(e) =>
                          handleChange(
                            "state",
                            e.target.value
                          )
                        }
                      />

                    </div>

                    <div>

                      <Label>
                        Zipcode *
                      </Label>

                      <Input
                        placeholder="Zipcode"
                        value={settings.zipcode}
                        onChange={(e) =>
                          handleChange(
                            "zipcode",
                            e.target.value
                          )
                        }
                      />

                    </div>

                    <div>

                      <Label>
                        Country ISO2 *
                      </Label>

                      <Input
                        placeholder="AU"
                        value={settings.country_iso2}
                        onChange={(e) =>
                          handleChange(
                            "country_iso2",
                            e.target.value.toUpperCase()
                          )
                        }
                      />

                    </div>

                  </div>

                </div>

                {/* UBER SECTION */}

                <div>

                  <h2 className="text-lg font-semibold mb-4">
                    Uber Credentials
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                    <div>

                      <Label>
                        Uber Client ID *
                      </Label>

                      <Input
                        placeholder="Uber Client ID"
                        value={settings.uber_client_id}
                        onChange={(e) =>
                          handleChange(
                            "uber_client_id",
                            e.target.value
                          )
                        }
                      />

                    </div>

                    <div>

                      <Label>
                        Uber Customer ID *
                      </Label>

                      <Input
                        placeholder="cus_xxxxxxxxx"
                        value={settings.uber_customer_id}
                        onChange={(e) =>
                          handleChange(
                            "uber_customer_id",
                            e.target.value
                          )
                        }
                      />

                    </div>

                    <div>

                      <Label>
                        Uber Client Secret *
                      </Label>

                      <Input
                        type="password"
                        placeholder="Uber Client Secret"
                        value={settings.uber_client_secret}
                        onChange={(e) =>
                          handleChange(
                            "uber_client_secret",
                            e.target.value
                          )
                        }
                      />

                    </div>

                    <div>

                      <Label>
                        Uber Webhook Secret *
                      </Label>

                      <Input
                        type="password"
                        placeholder="Webhook Secret"
                        value={settings.uber_webhook_secret}
                        onChange={(e) =>
                          handleChange(
                            "uber_webhook_secret",
                            e.target.value
                          )
                        }
                      />

                    </div>

                  </div>

                </div>

                {/* SAVE BUTTON */}

                <div className="mt-8">

                  <Button
                    onClick={saveUberSettings}
                    disabled={saving}
                    className="w-full h-12 text-lg"
                  >

                    {saving ? (

                      <>

                        <Loader2
                          className="animate-spin mr-2"
                          size={18}
                        />

                        Saving Configuration...

                      </>

                    ) : (

                      <>

                        <Truck
                          className="mr-2"
                          size={18}
                        />

                        Save Uber Configuration

                      </>

                    )}

                  </Button>

                </div>

              </CardContent>

            </Card>

          </>
        )}

      </div>

    </AdminLayout>
  );
}